import { Alert, Box, TextField, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Link as RouterLink, useParams } from 'react-router-dom';
import EurCurrencyField from '@/components/form/EurCurrencyField.tsx';
import FormSection from '@/features/investments/form/components/FormSection.tsx';
import { InvestmentFormData } from '@/features/investments/form/investment-form.schema.ts';

const dateFieldSx = {
  '& .MuiInputBase-input': {
    padding: '16.5px 14px',
  },
  '& input[type="date"]': {
    textAlign: 'left',
  },
  '& input[type="date"]::-webkit-date-and-time-value': {
    textAlign: 'left',
  },
  '& input[type="date"]::-webkit-datetime-edit': {
    padding: 0,
  },
  '& input[type="date"]::-webkit-datetime-edit-fields-wrapper': {
    padding: 0,
  },
};

interface IncomeSectionProps {
  hasRentalHistory?: boolean;
}

const IncomeSection = ({ hasRentalHistory = false }: IncomeSectionProps) => {
  const { investmentId } = useParams<{ investmentId: string }>();
  const {
    control,
    formState: { errors },
  } = useFormContext<InvestmentFormData>();

  const status = useWatch({ control, name: 'status' });
  const investmentType = useWatch({ control, name: 'type' });
  const isRented = status === 'RENTED';
  const isRoomRent = investmentType === 'ROOM_RENT';
  const lockedHelperText =
    'La renta actual se calcula desde el historial. Modifícala en la pestaña Alquileres.';

  return (
    <FormSection
      title="4. Ingresos"
      description={
        hasRentalHistory
          ? 'Renta actual sincronizada con el histórico de alquileres.'
          : 'Renta actual y fecha de inicio del alquiler.'
      }
    >
      {isRoomRent && (
        <Alert severity="info">
          La renta se calculará desde los contratos de cada habitación. Configúralos en la pestaña
          Alquileres después de crear la inversión.
        </Alert>
      )}
      {hasRentalHistory && (
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title={lockedHelperText}>
            <InfoOutlinedIcon color="info" fontSize="small" />
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            La renta actual se calcula desde el historial. Modifícala en la pestaña{' '}
            {investmentId ? (
              <Typography
                component={RouterLink}
                to={`/inversiones/${investmentId}?tab=rental-history`}
                variant="body2"
                color="primary"
                sx={{ fontWeight: 600, textDecoration: 'none' }}
              >
                Alquileres
              </Typography>
            ) : (
              'Alquileres'
            )}
            .
          </Typography>
        </Box>
      )}

      {!hasRentalHistory && (
        <Alert severity="info">
          Al crear la inversión se guardará esta renta como periodo actual. Podrás añadir alquileres
          anteriores o modificar el historial desde la pestaña Alquileres.
        </Alert>
      )}

      {!isRoomRent && (
        <Controller
          name="monthlyRent"
          control={control}
          render={({ field: { value, onChange } }) => (
            <EurCurrencyField
              label="Renta mensual actual"
              value={value}
              onChange={onChange}
              disabled={hasRentalHistory}
              error={!!errors.monthlyRent}
              helperText={
                hasRentalHistory
                  ? lockedHelperText
                  : (errors.monthlyRent?.message ??
                    (isRented ? 'Opcional. Complétala si el activo está alquilado' : 'Opcional'))
              }
            />
          )}
        />
      )}

      {!isRoomRent && (
        <Controller
          name="rentedSince"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Fecha inicio alquiler"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={hasRentalHistory}
              sx={dateFieldSx}
              helperText={hasRentalHistory ? lockedHelperText : 'Opcional'}
            />
          )}
        />
      )}
    </FormSection>
  );
};

export default IncomeSection;
